import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { extend, override } from 'flarum/common/extend';
import Stream from 'flarum/common/utils/Stream';
import Button from 'flarum/common/components/Button';
import ComposerState from 'flarum/forum/states/ComposerState';
import app from 'flarum/forum/app';
import deepEqual from './utils/deepEqual';
import fillRelationship from './utils/fillRelationship';
export default function () {
  // Add changed() method to ComposerState
  ComposerState.prototype['changed'] = function () {
    if (!this.body || !this.data) return false;
    const currentData = this.data();
    const draft = this.draft;
    const fields = Object.keys(currentData).filter(fieldName => fieldName !== 'relationships');
    if (!fields || fields.length === 0) {
      return false;
    }

    // If there's no content, we don't want to save this draft
    // regardless of whether other attributes have changed.
    if (!this.fields.content()) {
      return false;
    }
    const getData = fieldKey => (fieldKey === 'content' ? this.fields.content() : currentData[fieldKey]) || '';
    for (const field of fields) {
      var _draft$data;
      const fieldValue = getData(field);
      const draftFieldValue = draft == null || (_draft$data = draft.data) == null || (_draft$data = _draft$data.attributes) == null ? void 0 : _draft$data[field];
      if (!draft && fieldValue || draft && !deepEqual(fieldValue, draftFieldValue)) {
        return true;
      }
    }

    // Check if relationships exist and need comparison
    if (!currentData.relationships && (!draft || !draft.relationships())) {
      return false;
    }

    // If only data has relationships but no draft exists, consider it changed
    if (currentData.relationships && !draft) {
      return true;
    }

    // If no relationships in data, no changes to check
    if (!currentData.relationships) {
      return false;
    }
    const relationships = Object.keys(currentData.relationships);
    const equalRelationships = (composerData, draftModel, relName) => {
      var _draftModel$relations, _draftModel$relations2;
      if ((!composerData.relationships[relName] || !composerData.relationships[relName].length) && (!(relName in draftModel.relationships()) || !((_draftModel$relations = draftModel.relationships()[relName].data) != null && _draftModel$relations.length))) {
        return true;
      } else if (!(relName in draftModel.relationships()) || composerData.relationships[relName].length !== ((_draftModel$relations2 = draftModel.relationships()[relName].data) == null ? void 0 : _draftModel$relations2.length)) {
        return false;
      }
      const getId = relItem => typeof relItem.id == 'function' ? relItem.id() : relItem.id;
      const dataIds = fillRelationship(composerData.relationships[relName], getId);
      const draftIds = fillRelationship(draftModel.relationships()[relName].data, getId);
      const dataIdsArray = Array.isArray(dataIds) ? dataIds : [dataIds];
      const draftIdsArray = Array.isArray(draftIds) ? draftIds : [draftIds];
      return !dataIdsArray.some((id, i) => id !== draftIdsArray[i]);
    };
    for (const relationship of relationships) {
      if (!draft) {
        if (currentData.relationships[relationship]) {
          return true;
        }
      } else {
        if (!equalRelationships(currentData, draft, relationship)) {
          return true;
        }
      }
    }
    return false;
  };

  // Add saveDraft() method to ComposerState
  ComposerState.prototype['saveDraft'] = function () {
    this.saving = true;
    m.redraw();
    const afterSave = () => {
      this.saving = false;
      this.justSaved = true;
      setTimeout(() => {
        this.justSaved = false;
        m.redraw();
      }, 300);
      m.redraw();
    };
    const draft = this.draft;
    if (draft && draft.id() && !draft.exists) {
      // Draft was deleted before autosave, no need to save.
      return;
    }

    // Helper to serialize relationships for API and filter read-only fields
    const serializeRelationships = draftData => {
      const serialized = _objectSpread({}, draftData);

      // Remove read-only fields that shouldn't be sent to API
      delete serialized.scheduledValidationError;
      delete serialized.updatedAt;
      if (draftData.relationships) {
        serialized.relationships = {};
        Object.keys(draftData.relationships).forEach(relationshipKey => {
          const relationship = draftData.relationships[relationshipKey];
          if (Array.isArray(relationship)) {
            // Convert array of models to JSON:API format with data wrapper
            serialized.relationships[relationshipKey] = {
              data: relationship.map(relModel => {
                var _relModel$data, _relModel$data2, _relModel$type;
                return {
                  type: typeof ((_relModel$data = relModel.data) == null ? void 0 : _relModel$data.type) === 'function' ? relModel.data.type() : ((_relModel$data2 = relModel.data) == null ? void 0 : _relModel$data2.type) || ((_relModel$type = relModel.type) == null ? void 0 : _relModel$type.call(relModel)) || 'unknown',
                  id: typeof relModel.id === 'function' ? relModel.id() : relModel.id
                };
              })
            };
          } else if (relationship && typeof relationship === 'object') {
            var _relationship$data, _relationship$data2, _relationship$type;
            // Convert single model to JSON:API format with data wrapper
            serialized.relationships[relationshipKey] = {
              data: {
                type: typeof ((_relationship$data = relationship.data) == null ? void 0 : _relationship$data.type) === 'function' ? relationship.data.type() : ((_relationship$data2 = relationship.data) == null ? void 0 : _relationship$data2.type) || ((_relationship$type = relationship.type) == null ? void 0 : _relationship$type.call(relationship)) || 'unknown',
                id: typeof relationship.id === 'function' ? relationship.id() : relationship.id
              }
            };
          }
        });
      }
      return serialized;
    };
    if (draft) {
      var _this$data;
      const rawData = (_this$data = this.data) == null ? void 0 : _this$data.call(this);
      if (!rawData) return;
      const updatePayload = serializeRelationships(rawData);
      draft.save(updatePayload).catch(saveError => {
        console.error('Draft save failed:', saveError);
        console.error('Response:', saveError.response);
      }).then(() => afterSave());
    } else {
      var _this$data2;
      const rawData = (_this$data2 = this.data) == null ? void 0 : _this$data2.call(this);
      if (!rawData) return;
      const createPayload = serializeRelationships(rawData);
      app.store.createRecord('drafts').save(createPayload).then(savedDraft => {
        savedDraft.loadRelationships(true);
        this.draft = savedDraft;
        afterSave();
      }).catch(createError => {
        console.error('New draft save failed:', createError);
        console.error('Response:', createError.response);
      });
    }
  };

  // Add save draft button to composer
  extend('flarum/forum/components/Composer', 'controlItems', function (items) {
    if (!(this.state.bodyMatches('flarum/forum/components/DiscussionComposer') || this.state.bodyMatches('flarum/forum/components/ReplyComposer')) || !app.forum.attribute('canSaveDrafts') || this.state.position === 'minimized') return;
    const classNames = ['Button', 'Button--icon', 'Button--link'];
    if (this.state.saving) {
      classNames.push('saving');
    }
    if (this.state.justSaved) {
      classNames.push('justSaved');
    }
    items.add('save-draft', m(Button, {
      icon: this.state.justSaved ? 'fas fa-check' : this.state.saving ? 'fas fa-spinner fa-spin' : 'fas fa-save',
      className: classNames.join(' '),
      itemClassName: "App-backControl",
      title: app.translator.trans('fof-drafts.forum.composer.title'),
      "aria-label": app.translator.trans('fof-drafts.forum.composer.title'),
      disabled: this.state.saving || this.state.justSaved || this.loading,
      onclick: this.state.saveDraft.bind(this.state)
    }), 20);
  });

  // Set up autosave
  extend(ComposerState.prototype, 'load', function () {
    var _app$session$user;
    if (!app.forum.attribute('canSaveDrafts')) return;
    if (
    // @ts-ignore - User preferences access
    (_app$session$user = app.session.user) != null && _app$session$user.preferences().draftAutosaveEnable && (this.bodyMatches('flarum/forum/components/DiscussionComposer') || this.bodyMatches('flarum/forum/components/ReplyComposer'))) {
      var _app$session$user2;
      this.autosaveInterval = setInterval(() => {
        var _this$changed;
        if ((_this$changed = this.changed) != null && _this$changed.call(this) && !this.saving && !this.loading) {
          var _this$saveDraft;
          (_this$saveDraft = this.saveDraft) == null || _this$saveDraft.call(this);
        }
        // @ts-ignore - User preferences access
      }, 1000 * ((_app$session$user2 = app.session.user) == null ? void 0 : _app$session$user2.preferences().draftAutosaveInterval));
    }
  });

  // Clear draft on composer clear
  extend(ComposerState.prototype, 'clear', function () {
    this.draft = null;
    if (this.autosaveInterval) clearInterval(this.autosaveInterval);
  });

  // Override preventExit to handle drafts
  override(ComposerState.prototype, 'preventExit', function (original) {
    var _this$changed2;
    if (this.body && this.body.componentClass && this.draft) {
      this.body.attrs.confirmExit = app.translator.trans('fof-drafts.forum.composer.exit_alert');
    }
    let prevented = false;
    if ((_this$changed2 = this.changed) != null && _this$changed2.call(this)) {
      const result = original();
      prevented = result === true;
    }
    if (prevented) return prevented;
    if (!this.body || !this.body.componentClass) return;
    const draft = this.draft;
    if (draft && !draft.title() && !draft.content() && confirm(app.translator.trans('fof-drafts.forum.composer.discard_empty_draft_alert'))) {
      draft.delete();
    }
    return prevented;
  });

  // Initialize composer body with draft data
  function initComposerBody() {
    Object.keys(this.attrs).forEach(attrKey => {
      if (!['originalContent', 'title', 'user'].includes(attrKey)) {
        this[attrKey] = this.attrs[attrKey];
      } else if (attrKey === 'title') {
        this.title = Stream(this.attrs.title);
      }
    });
    if (this.data) {
      this.composer.data = this.data.bind(this);
    }
    if (this.attrs.draft) {
      this.composer.draft = this.attrs.draft;
    }
  }
  extend('flarum/forum/components/DiscussionComposer', 'oninit', initComposerBody);
  extend('flarum/forum/components/ReplyComposer', 'oninit', initComposerBody);

  // Delete drafts when submitted
  function deleteDraftsOnSubmit() {
    if (this.composer.draft) {
      this.composer.draft.delete();
    }
  }
  extend('flarum/forum/components/DiscussionComposer', 'onsubmit', deleteDraftsOnSubmit);
  extend('flarum/forum/components/ReplyComposer', 'onsubmit', deleteDraftsOnSubmit);

  // Handle byobu extension if present
  if ('fof-byobu' in flarum.extensions) {
    extend('ext:fof/byobu/forum/pages/discussions/PrivateDiscussionComposer', 'onsubmit', deleteDraftsOnSubmit);
  }
}